import { useEffect, useRef } from "react";
import { enviarReacao } from "../../services/mensagensApi";

const EMOJIS_PADRAO = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * Popover de reação exibido ao hover ou long-press em uma MessageBubble.
 * 6 emojis padrão + botão "+" com fallback simples (sem emoji-picker externo).
 *
 * @param {{
 *   conversaId: number,
 *   messageId: string,
 *   onReact: (emoji: string) => void,
 *   onClose: () => void,
 * }} props
 */
export default function ReactionPicker({ conversaId, messageId, onReact, onClose }) {
  const ref = useRef(null);

  // Fecha ao clicar fora do popover
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Fecha com ESC
  useEffect(() => {
    function handleEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  async function handleEmoji(emoji) {
    try {
      await enviarReacao(conversaId, messageId, emoji);
      onReact(emoji);
    } catch (_err) {
      // falha silenciosa — UX não trava
    } finally {
      onClose();
    }
  }

  function handleMais() {
    // Fallback: prompt simples pois não há emoji-picker instalado no projeto
    const emoji = window.prompt("Digite um emoji para reagir:");
    if (emoji && emoji.trim()) {
      handleEmoji(emoji.trim());
    } else {
      onClose();
    }
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Escolher reação"
      className="absolute z-50 bottom-full mb-1.5 left-0 flex items-center gap-0.5 bg-surface-1 border border-white/[0.08] rounded-full px-2 py-1.5 shadow-xl"
    >
      {EMOJIS_PADRAO.map((emoji) => (
        <button
          key={emoji}
          type="button"
          aria-label={`Reagir com ${emoji}`}
          onClick={() => handleEmoji(emoji)}
          className="text-lg leading-none hover:scale-125 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-full p-0.5"
        >
          {emoji}
        </button>
      ))}

      <button
        type="button"
        aria-label="Escolher outro emoji"
        onClick={handleMais}
        className="w-7 h-7 flex items-center justify-center text-xs font-bold text-ink-3 hover:text-ink-1 hover:bg-surface-3 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ml-0.5"
      >
        +
      </button>
    </div>
  );
}
